import { Component } from '@theme/component';

/**
 * Formulas comparison, mobile accordion.
 * Each card has a toggle button (shown below 768px) that opens its panel (comparison list,
 * "Réserver", WhatsApp). Only one formula is open at a time. The open state lives on the card
 * (`data-open`) and on the button (`aria-expanded`); CSS animates the panel.
 * Links to a card (`#formule-…`, e.g. from the packs section) also open it, on click and when
 * the page loads with that hash.
 *
 * @extends {Component}
 */
class AbsolutFormulas extends Component {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    document.addEventListener('click', this.#handleLinkClick, { signal });
    window.addEventListener('hashchange', () => this.#openFromHash(), { signal });
    this.#openFromHash();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  /**
   * Opens the clicked formula and closes the others, or closes it if it was open.
   * @param {Event} event - Click on a `.ab-formula__toggle` button
   */
  toggle(event) {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('.ab-formula__toggle');
    const item = button?.closest('.ab-formula');
    if (!(item instanceof HTMLElement)) return;

    this.open(item, !item.hasAttribute('data-open'));
  }

  /**
   * Opens (or closes) a formula card; opening closes the other cards.
   * @param {Element} item - Formula card
   * @param {boolean} [open] - Target state
   */
  open(item, open = true) {
    if (open) {
      for (const other of this.querySelectorAll('.ab-formula[data-open]')) {
        if (other !== item) this.#setOpen(other, false);
      }
    }
    this.#setOpen(item, open);
  }

  /** @param {MouseEvent} event */
  #handleLinkClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a[href*="#formule-"]');
    if (!(link instanceof HTMLAnchorElement)) return;

    const url = new URL(link.href, window.location.href);
    if (url.pathname !== window.location.pathname) return;

    const item = this.#findItem(url.hash);
    if (item) this.open(item);
  };

  #openFromHash() {
    const item = this.#findItem(window.location.hash);
    if (item) this.open(item);
  }

  /**
   * @param {string} hash - Location hash, e.g. "#formule-signature-absolut"
   * @returns {Element | null}
   */
  #findItem(hash) {
    if (!hash.startsWith('#formule-')) return null;
    const item = this.querySelector(`#${CSS.escape(decodeURIComponent(hash.slice(1)))}`);
    return item?.classList.contains('ab-formula') ? item : null;
  }

  /**
   * @param {Element} item - Formula card
   * @param {boolean} open - Target state
   */
  #setOpen(item, open) {
    item.toggleAttribute('data-open', open);
    item.querySelector('.ab-formula__toggle')?.setAttribute('aria-expanded', String(open));
  }
}

if (!customElements.get('absolut-formulas')) {
  customElements.define('absolut-formulas', AbsolutFormulas);
}
